import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useCreateProgressUpdate } from "@/hooks/use-progress-updates";
import { FormError } from "@/components/shared/form-error";

const createProgressUpdateSchema = z.object({
  content: z.string().min(1, "Content is required").max(10000),
});

type CreateProgressUpdateForm = z.infer<typeof createProgressUpdateSchema>;

interface CreateProgressUpdateDialogProps {
  krId: string;
}

export function CreateProgressUpdateDialog({ krId }: CreateProgressUpdateDialogProps) {
  const [open, setOpen] = useState(false);
  const createProgressUpdate = useCreateProgressUpdate(krId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateProgressUpdateForm>({
    resolver: zodResolver(createProgressUpdateSchema),
    defaultValues: { content: "" },
  });

  const onSubmit = (data: CreateProgressUpdateForm) => {
    createProgressUpdate.mutate(data, {
      onSuccess: () => {
        setOpen(false);
        reset();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Add Progress Update</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Progress Update</DialogTitle>
          <DialogDescription>
            Write a progress update for this key result. It will be created as a draft
            that you can finalize later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pu-content">Content</Label>
            <Textarea
              id="pu-content"
              placeholder="Describe progress, blockers, next steps..."
              rows={6}
              {...register("content")}
            />
            <FormError message={errors.content?.message} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createProgressUpdate.isPending}>
              {createProgressUpdate.isPending ? "Creating..." : "Create Draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
